import { OktaAuth } from '@okta/okta-auth-js';
import { envConfig } from '../config/env.config';

export interface TokenRefreshConfig {
  refreshBufferMinutes: number;
  autoRefreshEnabled: boolean;
  maxRetryAttempts: number;
  retryDelayMs: number;
}

export class TokenRefreshService {
  private oktaAuth: OktaAuth;
  private config: TokenRefreshConfig;
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;

  constructor(oktaAuth: OktaAuth, config?: Partial<TokenRefreshConfig>) {
    this.oktaAuth = oktaAuth;
    this.config = {
      refreshBufferMinutes: envConfig.tokenRefreshBufferMinutes,
      autoRefreshEnabled: envConfig.autoTokenRefreshEnabled,
      maxRetryAttempts: 3,
      retryDelayMs: 1000,
      ...config,
    };
  }

  /**
   * Start automatic token refresh monitoring
   */
  public startAutoRefresh(): void {
    if (!this.config.autoRefreshEnabled) {
      return;
    }

    this.scheduleNextRefresh();
  }

  /**
   * Stop automatic token refresh monitoring
   */
  public stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Manually refresh tokens if needed
   */
  public async refreshTokensIfNeeded(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      // If already refreshing, wait for the current refresh to complete
      await this.refreshPromise;
      return true;
    }

    if (this.isRefreshing) {
      return false;
    }

    try {
      const tokenManager = this.oktaAuth.tokenManager;
      const accessToken = tokenManager.get('accessToken');
      const idToken = tokenManager.get('idToken');

      if (!accessToken && !idToken) {
        return false;
      }

      const needsRefresh = await this.checkIfTokensNeedRefresh();
      
      if (needsRefresh) {
        this.refreshPromise = this.performTokenRefresh();
        await this.refreshPromise;
        this.refreshPromise = null;
        return true;
      }

      return false;
    } catch (error) {
      this.refreshPromise = null;
      return false;
    }
  }

  /**
   * Get time until next token expiration
   */
  public async getTimeUntilExpiration(): Promise<number | null> {
    try {
      const tokenManager = this.oktaAuth.tokenManager;
      const accessToken = await tokenManager.get('accessToken');
      const idToken = await tokenManager.get('idToken');

      if (!accessToken && !idToken) {
        return null;
      }

      // Get the earliest expiration time
      let earliestExpiration = Infinity;

      if (accessToken?.expiresAt) {
        earliestExpiration = Math.min(earliestExpiration, accessToken.expiresAt);
      }

      if (idToken?.expiresAt) {
        earliestExpiration = Math.min(earliestExpiration, idToken.expiresAt);
      }

      if (earliestExpiration === Infinity) {
        return null;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      return Math.max(0, earliestExpiration - currentTime);
    } catch (error) {
      console.error('Error getting token expiration time:', error);
      return null;
    }
  }

  /**
   * Check if tokens need refresh based on buffer time
   */
  private async checkIfTokensNeedRefresh(): Promise<boolean> {
    try {
      const timeUntilExpiration = await this.getTimeUntilExpiration();
      
      if (timeUntilExpiration === null) {
        return false;
      }

      const bufferSeconds = this.config.refreshBufferMinutes * 60;
      const needsRefresh = timeUntilExpiration <= bufferSeconds;

      return needsRefresh;
    } catch (error) {
      return false;
    }
  }

  /**
   * Perform the actual token refresh with retry logic
   */
  private async performTokenRefresh(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.config.maxRetryAttempts; attempt++) {
      try {
        const tokenManager = this.oktaAuth.tokenManager;
        
        // Try to refresh both access and ID tokens
        const refreshPromises: Promise<any>[] = [];
        
        const accessToken = await tokenManager.get('accessToken');
        const idToken = await tokenManager.get('idToken');

        if (accessToken) {
          refreshPromises.push(tokenManager.renew('accessToken'));
        }

        if (idToken) {
          refreshPromises.push(tokenManager.renew('idToken'));
        }

        if (refreshPromises.length === 0) {
          this.isRefreshing = false;
          return;
        }

        await Promise.all(refreshPromises);
        
        this.isRefreshing = false;
        
        // Schedule next refresh
        this.scheduleNextRefresh();
        return;

      } catch (error) {
        lastError = error as Error;

        if (attempt < this.config.maxRetryAttempts) {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, this.config.retryDelayMs * attempt));
        }
      }
    }

    this.isRefreshing = false;
    
    // If all attempts failed, handle the error
    if (lastError) {
      this.handleRefreshFailure(lastError);
    }
  }

  /**
   * Schedule the next token refresh check
   */
  private scheduleNextRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Check every minute for tokens that need refresh
    const checkIntervalMs = 60 * 1000; // 1 minute

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshTokensIfNeeded();
      } catch (error) {
        console.error('Error during scheduled token refresh:', error);
      }
      
      // Schedule next check
      this.scheduleNextRefresh();
    }, checkIntervalMs);
  }

  /**
   * Handle token refresh failure
   */
  private handleRefreshFailure(error: Error): void {
    // Emit a custom event that the AuthContext can listen to
    const refreshFailedEvent = new CustomEvent('tokenRefreshFailed', {
      detail: { error }
    });
    window.dispatchEvent(refreshFailedEvent);
  }

  /**
   * Get current refresh status
   */
  public getRefreshStatus(): {
    isRefreshing: boolean;
    autoRefreshEnabled: boolean;
    nextCheckIn: number | null;
  } {
    const nextCheckIn = this.refreshTimer ? 60 : null; // Approximate time until next check
    
    return {
      isRefreshing: this.isRefreshing,
      autoRefreshEnabled: this.config.autoRefreshEnabled,
      nextCheckIn,
    };
  }
}

export default TokenRefreshService;